import React, { useEffect, useState } from 'react'
import MUIDrawer from '@mui/material/Drawer'
import styled from 'styled-components'

import { DrawerLockingContextProvider } from '~/views/components/WithDrawerLockingContext'
import { useDrawerOpenContext } from '~/views/contexts/DrawerOpenContext'

interface DrawerProps {
  children: React.ReactNode
  onClose: () => void
  title: string
  open: boolean
  width?: number
  'data-testid'?: string
  dataTestId?: string
}

interface DrawerLayoutProps {
  width?: number
}

interface DrawerState {
  disableBackdropClick: boolean
}

const DrawerLayout = styled.div<DrawerLayoutProps>`
  width: ${({ width }) => (width ? `${width}px` : '500px')};
  height: 100vh;
  display: flex;
  flex-direction: column;
`

const Drawer: React.FC<DrawerProps> = (props) => {
  const [state, setState] = useState<DrawerState>({
    disableBackdropClick: false,
  })

  const disableBackdropClick = () => {
    setState({ disableBackdropClick: true })
  }

  const enableBackdropClick = () => {
    setState({ disableBackdropClick: false })
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    // If a select menu is open we have probably
    // disabled the backdrop click, re-enable it
    // on the a tab away from the element
    if (event.which === 9) {
      enableBackdropClick()
    }
  }

  const { onChange: onChangeDrawer } = useDrawerOpenContext()

  const { width, open } = props

  useEffect(() => {
    if (open) {
      const drawerWidth = (width || 420) + 20
      if (onChangeDrawer) {
        onChangeDrawer(open, drawerWidth)
      }
    }
    return () => {
      if (onChangeDrawer) {
        onChangeDrawer(false, 0)
      }
    }
  }, [width, open, onChangeDrawer])

  const handleClose = (
    _event: unknown,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'escapeKeyDown') return false

    if (state.disableBackdropClick && reason === 'backdropClick') {
      // If a select menu is open we have probably
      // disabled the backdrop click, re-enable it
      // on the first click
      return enableBackdropClick()
    }

    props.onClose()
  }

  return (
    <DrawerLockingContextProvider
      value={{
        disableBackdropClick: disableBackdropClick,
        enableBackdropClick: enableBackdropClick,
        handleKeyDown: handleKeyDown,
      }}
    >
      <MUIDrawer
        disableEnforceFocus
        anchor="right"
        title={props.title}
        open={props.open}
        onClose={handleClose}
        PaperProps={{
          style: {
            overflowY: 'hidden',
          },
        }}
        data-testid={props['data-testid'] || props.dataTestId || 'drawer'}
      >
        <DrawerLayout width={width}>{props.children}</DrawerLayout>
      </MUIDrawer>
    </DrawerLockingContextProvider>
  )
}

export default Drawer
